import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useShiftProcedureStore, useForm } from "../../../../hooks";
import { InputForm, SelectForm, LoadingData, ErrorMessage } from "../../../ui";
import { useFormMessages } from "../../../hooks";

const initialFormData = {
  doctorId: "",
  procedureTime: "",
  patientName: "",
  patientDocument: "",
  procedureType: "",
  procedureDone: "",
  location: "",
  hasComplications: "",
  anesthesiaTech: "",
};

const initialFormValidations = {
  doctorId: [(value) => value.length > 0, "Selecciona un doctor"],
  procedureTime: [(value) => value.length > 0, "Diligencia una fecha y hora"],
  patientName: [(value) => value.length > 0, "Escribe el nombre del paciente"],
  patientDocument: [
    (value) => value.length > 0,
    "Escribe el documento del paciente",
  ],
  procedureType: [
    (value) => value.length > 0,
    "Selecciona el tipo de procedimiento",
  ],
  procedureDone: [
    (value) => value.length > 0,
    "Escribe el procedimiento realizado",
  ],
  location: [(value) => value.length > 0, "Escribe la localizacion"],
  hasComplications: [(value) => value.length > 0, "Selecciona una opción"],
  anesthesiaTech: [(value) => value.length > 0, "Selecciona una opción"],
};

export const AddShiftProcedureModal = ({ isOpen, setIsOpen }) => {
  // Datos de hooks ----------------------------------------//
  const {
    // Props
    doctorsSelect,
    procedureTypes,
    message,
    isLoading,
    anesthesiaTechniques,

    // Methods
    getDoctorsBySelect,
    startGetAnesthesiaQuestions,
    startAddShiftProcedure,
  } = useShiftProcedureStore();

  // States ------------------------------------------------//
  const [validateForm, setValidateForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formValidations, setFormValidations] = useState(
    initialFormValidations
  );
  const [anesthesiaQuestions, setAnesthesiaQuestions] = useState([]);

  // Formulario --------------------------------------------//
  const { formState, formValidation, onInputChange, isFormValid, onResetForm } =
    useForm(formData, formValidations);

  const {
    setFormSubmitted,
    showErrorMessage,
    showSuccessMessage,
    messageText,
  } = useFormMessages({
    message,
    setValidateForm,
  });

  // Funciones ---------------------------------------------//
  const onSubmit = () => {
    setValidateForm(true);
    if (isFormValid) {
      setFormSubmitted(true);
      startAddShiftProcedure(formState);
    }
  };

  const hasComplicationsChange = (event) => {
    const value = event.target.value;

    setFormData(() => {
      const updated = {
        ...formState,
        hasComplications: value,
      };

      if (value === "true") {
        updated.complicationText = "";
      } else {
        delete updated.complicationText;
      }

      return updated;
    });

    setFormValidations(() => {
      const updated = {
        ...formValidations,
      };

      if (value === "true") {
        updated.complicationText = [
          (val) => (val ?? "").toString().trim().length > 0,
          "Escribe las complicaciones",
        ];
      } else {
        delete updated.complicationText;
      }

      return updated;
    });
  };

  const anesthesiaTechChange = async (event) => {
    const value = event.target.value;

    // Actualiza solo anesthesiaTech
    setFormData(() => ({
      ...formState,
      anesthesiaTech: value,
    }));

    const response = await startGetAnesthesiaQuestions(value);

    // console.log("response", response);

    if (Array.isArray(response)) {
      setAnesthesiaQuestions(response);

      const newAnswers = response.reduce((acc, { question }) => {
        acc[question.id] = "";
        return acc;
      }, {});

      const newValidations = response.reduce((acc, { question }) => {
        acc[question.id] = [
          (val) => (val ?? "").toString().trim().length > 0,
          "Esta pregunta es obligatoria",
        ];
        return acc;
      }, {});

      // Agrega respuestas nuevas al formState
      setFormData(() => ({
        ...formState,
        anesthesiaTech: value,
        ...newAnswers,
      }));

      // Agrega validaciones nuevas
      setFormValidations(() => ({
        ...formValidations,
        ...newValidations,
      }));

      return;
    }

    // Si no hay preguntas, limpiar respuestas y validaciones previas
    const prevQuestionIds = anesthesiaQuestions.map((q) => q.question.id);

    setFormData(() => {
      const updated = {
        ...formState,
        anesthesiaTech: value,
      };
      prevQuestionIds.forEach((id) => delete updated[id]);
      return updated;
    });

    setFormValidations(() => {
      const updated = { ...formValidations };
      prevQuestionIds.forEach((id) => delete updated[id]);
      return updated;
    });

    setAnesthesiaQuestions([]);
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    if (doctorsSelect.data.length === 0 && isOpen) {
      getDoctorsBySelect();
    }
  }, [doctorsSelect.data, getDoctorsBySelect, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setFormValidations(initialFormValidations);
      setAnesthesiaQuestions([]);
      setValidateForm(false);
      setFormSubmitted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (showSuccessMessage) {
      setIsOpen(false);
    }
  }, [showSuccessMessage]);

  //   useEffect(() => {
  //     console.log("anesthesiaQuestions", anesthesiaQuestions);
  //   }, [anesthesiaQuestions]);

  //   useEffect(() => {
  //     console.log("formState", formState);
  //   }, [formState]);

  //   useEffect(() => {
  //     console.log("formValidations", formValidations);
  //   }, [formValidations]);

  //   useEffect(() => {
  //     console.log("formValidation", formValidation);
  //   }, [formValidation]);

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        setIsOpen(false);
      }}
      centered
      size="md"
    >
      <Modal.Header closeButton>
        <h5 className="modal-title">Agregar Procedimiento</h5>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          {/* Formulario estatico */}
          <>
            <div className="col-md-12">
              <SelectForm
                label={"Doctor"}
                sublabel={"Campo obligatorio"}
                options={doctorsSelect.data}
                labelKey={"name"}
                validation={formValidation.doctorIdValid}
                submit={validateForm}
                name={"doctorId"}
                value={formState.doctorId}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <InputForm
                label={"Fecha y hora"}
                sublabel={"Campo obligatorio"}
                type={"datetime-local"}
                placeholder={"Fecha"}
                validation={formValidation.procedureTimeValid}
                submit={validateForm}
                name={"procedureTime"}
                value={formState.procedureTime}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <InputForm
                label={"Nombre completo del paciente"}
                sublabel={"Campo obligatorio"}
                type={"text"}
                placeholder={"Nombre completo del paciente"}
                validation={formValidation.patientNameValid}
                submit={validateForm}
                name={"patientName"}
                value={formState.patientName}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <InputForm
                label={"Documento del paciente"}
                sublabel={"Campo obligatorio"}
                type={"text"}
                placeholder={"Documento del paciente"}
                validation={formValidation.patientDocumentValid}
                submit={validateForm}
                name={"patientDocument"}
                value={formState.patientDocument}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <SelectForm
                label={"Tipo de procedimiento"}
                sublabel={"Campo obligatorio"}
                options={procedureTypes}
                labelKey={"label"}
                validation={formValidation.procedureTypeValid}
                submit={validateForm}
                name={"procedureType"}
                value={formState.procedureType}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <InputForm
                label={"Procedimiento realizado"}
                sublabel={"Campo obligatorio"}
                type={"text"}
                placeholder={"Procedimiento realizado"}
                validation={formValidation.procedureDoneValid}
                submit={validateForm}
                name={"procedureDone"}
                value={formState.procedureDone}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <InputForm
                label={"Localización"}
                sublabel={"Campo obligatorio"}
                type={"text"}
                placeholder={"Localización"}
                validation={formValidation.locationValid}
                submit={validateForm}
                name={"location"}
                value={formState.location}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label">
                {"¿Tuvo complicaciones?"} <span className="text-danger"></span>
                <span className="text-danger" style={{ fontSize: "10px" }}>
                  {"Campo obligatorio"}
                </span>
              </label>
              <div className="custom-control custom-radio custom-control-inline">
                <div className="form-check mb-0">
                  <input
                    id="hasComplications-yes"
                    className="form-check-input me-2"
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                    }}
                    readOnly
                    type="radio"
                    name={`hasComplications`}
                    value={"true"}
                    checked={formState.hasComplications === "true"}
                    onChange={hasComplicationsChange}
                  />
                  <label
                    className="form-check-label"
                    style={{ fontSize: "18px" }}
                    htmlFor="hasComplications-yes"
                  >
                    Si
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    id="hasComplications-no"
                    className="form-check-input me-2"
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                    }}
                    readOnly
                    type="radio"
                    name={`hasComplications`}
                    value={"false"}
                    checked={formState.hasComplications === "false"}
                    onChange={hasComplicationsChange}
                  />
                  <label
                    className="form-check-label"
                    style={{ fontSize: "18px" }}
                    htmlFor="hasComplications-no"
                  >
                    No
                  </label>
                </div>
              </div>
              {formValidation.hasComplicationsValid && validateForm && (
                <span className="text-danger">
                  {formValidation.hasComplicationsValid}
                </span>
              )}
            </div>
            {formState.hasComplications === "true" && (
              <div className="col-md-12">
                <InputForm
                  label={"Complicaciones"}
                  sublabel={"Campo obligatorio"}
                  type={"text"}
                  placeholder={"Complicaciones"}
                  validation={formValidation.complicationTextValid}
                  submit={validateForm}
                  name={"complicationText"}
                  value={formState.complicationText}
                  onInputChange={onInputChange}
                />
              </div>
            )}
            <div className="col-md-12">
              <SelectForm
                label={"Tecnica anestesica"}
                sublabel={"Campo obligatorio"}
                options={anesthesiaTechniques}
                labelKey={"label"}
                validation={formValidation.anesthesiaTechValid}
                submit={validateForm}
                name={"anesthesiaTech"}
                value={formState.anesthesiaTech}
                onInputChange={anesthesiaTechChange}
              />
            </div>
            {anesthesiaQuestions.map(({ question }) =>
              question.multipleChoice && question.questionOptions.length > 0 ? (
                <div className="col-md-12" key={question.id}>
                  <label className="form-label">
                    {question.title}{" "}
                    <span className="text-danger" style={{ fontSize: "10px" }}>
                      Campo obligatorio
                    </span>
                  </label>
                  <div className="custom-control custom-radio custom-control-inline">
                    {question.questionOptions.map((option) => (
                      <div className="form-check mb-0" key={option.id}>
                        <input
                          id={`${question.id}-${option.id}`}
                          className="form-check-input me-2"
                          style={{
                            width: "1.5rem",
                            height: "1.5rem",
                          }}
                          type="radio"
                          name={question.id}
                          value={option.value}
                          checked={formState[question.id] === option.value}
                          onChange={onInputChange}
                        />
                        <label
                          className="form-check-label"
                          style={{ fontSize: "18px" }}
                          htmlFor={`${question.id}-${option.id}`}
                        >
                          {option.value}
                        </label>
                      </div>
                    ))}
                  </div>
                  {formValidation[`${question.id}Valid`] && validateForm && (
                    <span className="text-danger">
                      {formValidation[`${question.id}Valid`]}
                    </span>
                  )}
                </div>
              ) : (
                <InputForm
                  key={question.id}
                  label={question.title}
                  sublabel={"Campo obligatorio"}
                  type={"text"}
                  placeholder={question.title}
                  validation={formValidation[`${question.id}Valid`]}
                  submit={validateForm}
                  name={question.id}
                  value={formState[question.id] || ""}
                  onInputChange={onInputChange}
                />
              )
            )}
          </>

          {/* Mensajes */}
          <>
            {(isLoading || doctorsSelect.isLoading) && (
              <div className="col-md-12">
                <LoadingData />
              </div>
            )}
            {(showErrorMessage || doctorsSelect.message?.type === "error") && (
              <div className="col-md-12">
                <ErrorMessage>
                  {messageText || doctorsSelect.message.text}
                </ErrorMessage>
              </div>
            )}
          </>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onSubmit()}
          //   disabled={isLoading}
        >
          Registrar
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>
  );
};
